import { APP_STORE_URLS } from '@splitfiesta/shared';

import { isEmbeddedInMobileApp } from '@/lib/embeddedMode';

import { isAndroid, isIOSNonSafari, isStandalonePWA } from '@/utils/platform';

export type NudgeStore = 'ios' | 'android';

/** Shared by every nudge surface: "Not now" anywhere quiets all of them. */
export const NUDGE_DISMISSED_KEY = 'native_app_prompt_dismissed';
const DISMISSED_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export function wasDismissedRecently(): boolean {
  try {
    const dismissedAt = localStorage.getItem(NUDGE_DISMISSED_KEY);
    if (!dismissedAt) return false;
    return Date.now() - parseInt(dismissedAt, 10) < DISMISSED_DURATION_MS;
  } catch {
    return false;
  }
}

/** In-memory mirror of the dismissal so every mounted nudge reacts at once,
 *  and so a "Not now" still holds for the session when storage throws. */
let dismissedInSession = false;
const listeners = new Set<() => void>();

export function isDismissed(): boolean {
  return dismissedInSession || wasDismissedRecently();
}

export function rememberDismissal(): void {
  dismissedInSession = true;
  try {
    localStorage.setItem(NUDGE_DISMISSED_KEY, String(Date.now()));
  } catch {
    /* private mode: the in-memory flag carries the session */
  }
  listeners.forEach(notify => notify());
}

export function subscribeDismissal(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Tests only: clears the in-memory flag between cases. */
export function resetDismissalForTests(): void {
  dismissedInSession = false;
}

/**
 * The store to nudge toward in this browser, ignoring dismissal. Safari on
 * iOS gets nothing because it shows the Smart App Banner on its own; a page
 * opened from inside the app or an installed PWA must not sell the app.
 */
export function nudgeStoreForPlatform(): NudgeStore | null {
  if (isEmbeddedInMobileApp() || isStandalonePWA()) return null;
  if (isAndroid()) {
    return import.meta.env.VITE_PLAY_STORE_AVAILABLE === 'true'
      ? 'android'
      : null;
  }
  if (isIOSNonSafari()) {
    return import.meta.env.VITE_APP_STORE_AVAILABLE === 'true' ? 'ios' : null;
  }
  return null;
}

export function storeUrl(store: NudgeStore): string {
  return APP_STORE_URLS[store];
}

/** The bill link's install card: once per browser, separate from the nudges' 7-day dismissal. */
export const CLAIM_INSTALL_CARD_KEY = 'sf.claimInstallCardShown';
let claimInstallCardTaken = false;

/** True the first time in this browser, and records it; storage failures fall back to the session. */
export function takeClaimInstallCardTurn(): boolean {
  if (claimInstallCardTaken) return false;
  claimInstallCardTaken = true;
  try {
    if (localStorage.getItem(CLAIM_INSTALL_CARD_KEY) === '1') return false;
    localStorage.setItem(CLAIM_INSTALL_CARD_KEY, '1');
  } catch {
    /* private mode: the in-memory flag carries the session */
  }
  return true;
}

/** Tests only. */
export function resetClaimInstallCardForTests(): void {
  claimInstallCardTaken = false;
}
